import { useState, useEffect } from 'react';

type CameraPermissionState = {
  state: PermissionState,
  isLoading: boolean,
  error?: string
}

const useCamera = (requestIfNeeded: boolean = false): CameraPermissionState => {
  const [permission, setPermission] = useState<CameraPermissionState>({
    state: 'prompt',
    isLoading: true,
    error: undefined,
  });

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions) {
      setPermission({
        state: 'denied',
        isLoading: false,
        error: 'Permissions API is not supported',
      });
      return;
    }

    const queryPermission = async () => {
      setPermission((prev) => ({ ...prev, isLoading: true }));

      try {
        const permissionStatus = await navigator.permissions.query({ name: "camera" as PermissionName });
        setPermission({ state: permissionStatus.state, isLoading: false, error: undefined });

        permissionStatus.onchange = () => {
          setPermission({ state: permissionStatus.state, isLoading: false, error: undefined });
        };

        // If state is 'prompt' and requestIfNeeded is true, request the permission
        if (permissionStatus.state === 'prompt' && requestIfNeeded) {
          await navigator.mediaDevices.getUserMedia({ video: true });
          // Re-query the permission after request
          const updatedStatus = await navigator.permissions.query({ name: "camera" as PermissionName });
          setPermission({ state: updatedStatus.state, isLoading: false, error: undefined });
        }
      } catch (error) {
        setPermission({
          state: 'denied',
          isLoading: false,
          error:
            error instanceof Error
              ? `Error querying camera permission: ${error.message}`
              : 'Unknown error',
        });
      }
    };

    queryPermission();
  }, [requestIfNeeded]);

  return permission;
};

export { useCamera };
